import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Input from "../../../../../../../components/Input";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePastSurgicalHistoryCreate } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-create";
import { usePastSurgicalHistoryUpdate } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-update";
import { usePastSurgicalHistoryList } from "../../../../../../../queries/history/past-surgical/use-past-surgical-history-list";
import {
  ExportClinicalDataToEncounterTypeEnum,
  SurgicalHistory,
} from "../../../../../../../services";
import { useExportClinicalToEncounter } from "../../../../../../../queries/encounter/use-export-clinical-to-encounter";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../../utils/utils";
import {
  SURGERY_INITIAL_VALUES,
  SURGERY_VALIDATION_SCHEMA,
} from "./history-constant";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import { getStoredRealm } from "@/utils/auth-storage";
interface UpsertSurgicalHistoryDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertSurgicalHistoryDrawer = ({
  disclosureHost,
}: UpsertSurgicalHistoryDrawerProps) => {
  const prefillData = disclosureHost?.data as SurgicalHistory;
  const [selectedPatient] = useSelectedPatient();
  const { patientId } = useParams() as { patientId: any };
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createPastSurgicalHistory } =
    usePastSurgicalHistoryCreate();
  const { mutateAsync: updatePastSurgicalHistory } =
    usePastSurgicalHistoryUpdate();
  const { mutateAsync: exportClinical } = useExportClinicalToEncounter();

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [variant, setVariant] = useState<"import" | "default">("default");
  const [selectedRecords, setSelectedRecords] = useState<any>();

  const [surgicalPagination, setSurgicalPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: surgicalHistoryList, isFetching: isSurgicalHistoryFetching } =
    usePastSurgicalHistoryList({
      patientUuid: patientId ?? "",
      pagination: surgicalPagination,
      gridSorting: [],
      archive: false,
      status: true,
      enabled: variant === "import",
    });

  const surgicalHistoryOptions = useMemo(() => {
    const fetched =
      surgicalHistoryList?.data.content.map((item: any) => ({
        value: item.uuid,
        label: item?.surgeryName,
      })) ?? [];
    const selected = (selectedRecords ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => (o as any)[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [surgicalHistoryList?.data.content, selectedRecords]);

  const formik = useFormik<Omit<SurgicalHistory, "patient">>({
    initialValues: prefillData || SURGERY_INITIAL_VALUES,
    validationSchema: SURGERY_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values?: any) {
    const patient = {
      [patientId ? patientId : selectedPatient?.uuid]: patientId
        ? ""
        : selectedPatient?.firstName + " " + selectedPatient?.lastName,
    };

    const payload = {
      surgicalHistory: {
        ...values,
        patient,
        appointmentId: appointmentid ?? undefined,
      },
      xTENANTID: getStoredRealm() ?? undefined,
    };

    if (prefillData) showToastAndResetForm(updatePastSurgicalHistory(payload));
    else showToastAndResetForm(createPastSurgicalHistory(payload));
  }

  async function handleImportSurgicalHistory() {
    const recordUuids = (selectedRecords ?? [])
      .map((record: any) => record?.value)
      .filter(Boolean);

    if (!recordUuids.length) {
      showSnackbar(getErrorSnackbar("Please select at least one record."));
      return;
    }

    showToastAndResetForm(
      exportClinical({
        type: ExportClinicalDataToEncounterTypeEnum.SurgicalHistory,
        appointmentUuid: appointmentid ?? "",
        recordUuids,
        xTENANTID: getStoredRealm() ?? undefined,
      })
    );
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        closeAndResetForm();

        queryClient.invalidateQueries({
          queryKey: ["patient-surgical-history-list"],
        });

        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndResetForm() {
    formik.resetForm();
    setVariant("default");
    setSelectedRecords(undefined);
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle={
        prefillData ? "Edit Past Surgical History" : "Add Past Surgical History"
      }
      dialogAction={
        <StyledButtonV2
          label={variant === "import" ? "Import" : "Save"}
          variant="contained"
          onClick={
            variant === "import"
              ? handleImportSurgicalHistory
              : formik.submitForm
          }
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "450px",
        },
      }}
    >
      {isVisitNotePage && (
        <Box pt={1} px={1.5}>
          <LabeledSwitch
            label="import from existing records"
            checked={variant === "import"}
            onChange={(e) => {
              setVariant(e.target.checked ? "import" : "default");
            }}
          />
        </Box>
      )}

      {variant === "import" && (
        <Grid container spacing={3} pt={0}>
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              required
              loading={isSurgicalHistoryFetching}
              label="Surgery Name"
              optionWithCheckbox={true}
              placeholder="Select from Existing Surgical Records"
              options={surgicalHistoryOptions}
              value={selectedRecords}
              onChange={(v) => setSelectedRecords(v)}
              pagination={surgicalPagination}
              setPagination={setSurgicalPagination}
              totalElements={surgicalHistoryList?.data?.page?.totalElements}
            />
          </Grid>
        </Grid>
      )}

      {variant === "default" && (
        <Grid container spacing={3} pt={isVisitNotePage ? 1 : 2}>
          <Grid item xs={12}>
            <Input
              required
              label={"Surgery Name"}
              textFieldProps={{
                placeholder: "Enter Surgery Name",
                name: "surgeryName",
                value: formik.values.surgeryName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={formik.touched?.surgeryName && formik.errors?.surgeryName}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomDatePicker
              // required
              maxDate={dayjs(new Date())}
              name="surgeryDate"
              handleBlur={formik.handleBlur}
              placeholder="Choose Date"
              value={formik.values.surgeryDate}
              label="Date"
              onChange={(val) => formik.setFieldValue("surgeryDate", val)}
              error={formik.touched?.surgeryDate && formik.errors?.surgeryDate}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              label="Note"
              id="outlined-multiline-static"
              rows={5}
              variant="outlined"
              name="note"
              placeholder="Type here"
              onBlur={formik.handleBlur}
              value={formik.values?.note}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      )}
    </StyledFormModal>
  );
};

export default UpsertSurgicalHistoryDrawer;
